import { useRef } from "react";
import L from "leaflet";
import { Marker, Popup } from "react-leaflet";
import PropTypes from "prop-types";

import mapMarkerDelayed from "assets/images/mapMarkers/YellowMarker.svg";
import mapMarkerFallback from "assets/images/mapMarkers/BlackMarker.svg";
import mapMarkerIncomplete from "assets/images/mapMarkers/OrangeMarker.svg";
import mapMarkerInTransit from "assets/images/mapMarkers/BlueMarker.svg";
import Button from "components/atoms/Button/Button";
import { ReactComponent as CrossIcon } from "assets/images/icons/Cross.svg";
import { FormattedMessage } from "react-intl";

function Markers({ mapData, markerPosition }) {
  const markerRef = useRef(null);

  const handlePopupClose = () => {
    if (markerRef?.current?.close) {
      markerRef.current.close();
    }
  };

  const renderStatus = (status) => (
    <p
      className="m-0 rounded-pill font-weight-bold text-white"
      style={{
        padding: "3.5px 8px",
        backgroundColor: renderColor(status),
      }}
    >
      {status}
    </p>
  );

  const getMarkerIcon = (status) => {
    let iconUrl = "";
    switch (status) {
      case "In transit":
        iconUrl = mapMarkerInTransit;
        break;
      case "Delayed":
        iconUrl = mapMarkerDelayed;
        break;
      case "Incomplete":
        iconUrl = mapMarkerIncomplete;
        break;
      default:
        iconUrl = mapMarkerFallback;
        break;
    }
    return new L.Icon({
      iconUrl,
      iconSize: new L.Point(40, 47),
    });
  };

  const renderColor = (status) => {
    switch (status) {
      case "In transit":
        return "#298CDE";
      case "Delayed":
        return "#F1B92A";
      case "Incomplete":
        return "#EA7939";
      default:
        return "#252525";
    }
  };

  return (
    <>
      {mapData &&
        mapData.length > 0 &&
        mapData.map((shipment) => {
          if (!shipment.origin?.origin_coordinates || !shipment.delivery?.destination_coordinates) return null;
          const {
            origin: {
              origin_coordinates: { latitude: pickupLatitude, longitude: pickupLongitude },
              origin_city: originCity,
              origin_name: originName,
              origin_country: originCountry,
            },
            delivery: {
              destination_coordinates: { latitude: deliveryLatitude, longitude: deliveryLongitude },
              destination_city: destinationCity,
              destination_name: destinationName,
              destination_country: destinationCountry,
            },
            freight_mode: { freight_mode: freightMode },
            shipment_reference: shipmentId,
            shipment_title: shipmentTitle,
            payment_location: paymentLocation,
            status,
          } = shipment;

          const classStatus = status.toLowerCase().replace(" ", "-");

          const markerLatLon =
            markerPosition === "origin" ? [pickupLatitude, pickupLongitude] : [deliveryLatitude, deliveryLongitude];
          if (pickupLongitude && pickupLatitude && deliveryLongitude && deliveryLatitude)
            return (
              <Marker
                icon={getMarkerIcon(status)}
                key={shipmentId}
                position={markerLatLon}
                eventHandlers={{
                  click: () => {
                  },
                }}
                title={`${shipmentId} ${shipmentTitle}`}
              >
                <Popup ref={markerRef} className={`leaflet-pop-${classStatus}`}>
                  <div className="d-flex justify-content-between align-items-center">
                    {renderStatus(status)}
                    <div className="cursor-pointer" onClick={handlePopupClose} role="button" tabIndex={0}>
                      <CrossIcon />
                    </div>
                  </div>
                  <div className="d-flex align-items-center mb-0 w-100 justify-content-between mt-3">
                    <Button
                      variant="link"
                      className="mt-0 p-0 font-b9"
                      label={shipmentId}
                      onClick={() => {
                        window.open(`/shipment/${shipmentId}/tracking`, "_blank");
                      }}
                    />
                    <p className="font-b3 text-secondary m-0 text-right w-50">{shipmentTitle}</p>
                  </div>
                  <div className="d-flex align-items-center w-100 justify-content-between mb-3">
                    <p className="font-b3 m-0 text-secondary">{paymentLocation}</p>
                    <p className="font-b3 m-0 text-secondary">{freightMode}</p>
                  </div>
                  <div>
                    <p className="font-b4 m-0">
                      <FormattedMessage id="general.origin" defaultMessage="Origin" />
                    </p>
                    <p className="font-b2 mt-0">
                      {originName && `${originName}, `}
                      {originCity}, {originCountry}
                    </p>
                    <p className="font-b4 m-0">
                      <FormattedMessage id="general.destination" defaultMessage="Destination" />
                    </p>
                    <p className="font-b2 mb-0 mt-0">
                      {destinationName && `${destinationName}, `}
                      {destinationCity}, {destinationCountry}
                    </p>
                  </div>
                </Popup>
              </Marker>
            );
          return null;
        })}
    </>
  );
}

Markers.propTypes = {
  mapData: PropTypes.instanceOf(Object),
  markerPosition: PropTypes.string.isRequired,
};
Markers.defaultProps = {
  mapData: {},
};
export default Markers;
